import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCloudAuth } from "../../hooks/useCloudAuth";
import { ConfirmDialog } from "../../components/dashboard";
import { getMyInstance, deleteInstance, ApiError } from "../../lib/api/cloud";
import type { Instance } from "../../types/cloud";

export function AccountSettings() {
  const { t } = useTranslation();
  const { user } = useCloudAuth();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [showDeleteInstance, setShowDeleteInstance] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getMyInstance()
      .then(setInstance)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setInstance(null);
        }
      });
  }, []);

  const handleDeleteInstance = async () => {
    if (!instance) return;
    setDeleting(true);
    try {
      await deleteInstance(instance.id);
      setInstance(null);
    } catch {
      // Error handling
    } finally {
      setDeleting(false);
      setShowDeleteInstance(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">{t('dashboard.settings.title')}</h1>
        <p className="text-[#999] text-sm mt-1">{t('dashboard.settings.subtitle')}</p>
      </div>

      {/* Profile */}
      <section className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-[#f5f5f5] mb-4">{t('dashboard.settings.profile')}</h2>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-[#333]">
            <span className="text-[#999] text-sm">{t('dashboard.settings.email')}</span>
            <span className="text-[#f5f5f5] text-sm">{user?.email ?? "—"}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#333]">
            <span className="text-[#999] text-sm">{t('dashboard.settings.name')}</span>
            <span className="text-[#f5f5f5] text-sm">{user?.name ?? "—"}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#333]">
            <span className="text-[#999] text-sm">{t('dashboard.settings.role')}</span>
            <span className="text-[#f5f5f5] text-sm capitalize">{user?.role ?? "—"}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-[#999] text-sm">{t('dashboard.settings.status')}</span>
            <span className="text-[#f5f5f5] text-sm capitalize">{user?.status ?? "—"}</span>
          </div>
        </div>
        <p className="text-[#666] text-xs mt-4">
          {t('dashboard.settings.profileNote')}
        </p>
      </section>

      {/* Instance Info */}
      {instance && (
        <section className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#f5f5f5] mb-4">{t('dashboard.settings.instance')}</h2>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-[#333]">
              <span className="text-[#999] text-sm">{t('dashboard.settings.subdomain')}</span>
              <span className="text-[#f5f5f5] text-sm">{instance.subdomain}.cc.somebugs.com</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#333]">
              <span className="text-[#999] text-sm">{t('dashboard.settings.status')}</span>
              <span className="text-[#f5f5f5] text-sm capitalize">{instance.status}</span>
            </div>
            {instance.plan && (
              <div className="flex justify-between py-2 border-b border-[#333]">
                <span className="text-[#999] text-sm">{t('dashboard.settings.plan')}</span>
                <span className="text-[#f5f5f5] text-sm">{instance.plan.name}</span>
              </div>
            )}
            <div className="flex justify-between py-2">
              <span className="text-[#999] text-sm">{t('dashboard.settings.created')}</span>
              <span className="text-[#f5f5f5] text-sm">
                {new Date(instance.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Danger Zone */}
      {instance && (
        <section className="bg-[#1a1a1a] border border-red-500/20 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-400 mb-4">{t('dashboard.settings.dangerZone')}</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#f5f5f5] text-sm font-medium">{t('dashboard.settings.deleteInstance')}</p>
              <p className="text-[#999] text-xs">
                {t('dashboard.settings.deleteInstanceDescription')}
              </p>
            </div>
            <button
              onClick={() => setShowDeleteInstance(true)}
              disabled={deleting}
              className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-40"
            >
              {t('dashboard.settings.deleteInstance')}
            </button>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={showDeleteInstance}
        title={t('dashboard.instance.deleteConfirmTitle')}
        message={t('dashboard.settings.deleteInstanceConfirmMessage')}
        confirmText={t('dashboard.settings.deleteInstance')}
        danger
        confirmInput={instance?.subdomain}
        onConfirm={handleDeleteInstance}
        onCancel={() => setShowDeleteInstance(false)}
      />
    </div>
  );
}
